import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Banana as BananaIcon, Clock3 as Clock3Icon, Egg as EggIcon, ShieldPlus as ShieldPlusIcon, WheatOff as WheatOffIcon, BoxIcon } from 'lucide-react';
const categories = [{
  icon: EggIcon,
  title: 'High-Protein Starts',
  text: 'Egg-based dishes, cottage cheese bowls, and protein-packed omelettes to protect muscle mass'
}, {
  icon: ShieldPlusIcon,
  title: 'Anti-Inflammatory Options',
  text: 'Recipes loaded with antioxidants, omega-3s, and anti-inflammatory ingredients'
}, {
  icon: WheatOffIcon,
  title: 'Gluten-Free & Low-Carb',
  text: 'For those with sensitivities or who want to reduce bloating and belly weight'
}, {
  icon: BoxIcon,
  title: 'Prep the Night Before',
  text: 'Overnight oats, chia puddings, and batch options — ready when you wake up'
}, {
  icon: BananaIcon,
  title: 'Sweet Without the Sugar Crash',
  text: 'Naturally sweetened options that satisfy cravings without spiking blood sugar'
}, {
  icon: Clock3Icon,
  title: 'Ready in 5 Minutes',
  text: 'For your busiest mornings — no excuses, just results'
}];
export function RecipeCategories() {
  return <section className="bg-cream py-20 sm:py-24" data-id="element-152" data-mp="1xtkbm22w0j1z">
      <div className="mx-auto max-w-6xl px-5 lg:px-8" data-id="element-153" data-mp="22r2k5rczsw9g">
        <div className="max-w-2xl" data-id="element-154" data-mp="22qsuk4vv0r4a">
          <p className="eyebrow" data-id="element-155" data-mp="153lgboy3npov">Inside the guide</p>
          <h2 className="section-title mt-3" data-id="element-156" data-mp="154vcu9pb0pte">
            50 recipes organized for every morning, mood, and need
          </h2>
          <p className="mt-5 leading-7 text-forest/70" data-id="element-157" data-mp="1a4rbruj59r23">
            Each category is designed around what women 50+ actually need — not
            generic &quot;healthy eating&quot; advice:
          </p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-158" data-mp="2qgwvg4hnycrl">
          {categories.map(({
          icon: Icon,
          title,
          text
        }, index) => <article key={title} className="rounded-2xl border border-forest/10 bg-white p-6 transition hover:border-terracotta/40" data-id="element-159" data-mp="2veolsu2ntqto" data-mpi={index}>
              <div className="flex items-start justify-between" data-id="element-160" data-mp="qf6ut96bbqb2">
                <span className="text-3xl font-serif text-terracotta/60" data-id="element-161" data-mp="qh1c8didl0ae">
                  0{index + 1}
                </span>
                <span className="rounded-full bg-sage/30 p-3 text-olive" data-id="element-162" data-mp="10cbi5cbw9rtg">
                  <Icon size={20} data-id="element-163" data-mp="cna3368k3if7" />
                </span>
              </div>
              <h3 className="mt-8 text-lg font-extrabold text-forest" data-id="element-164" data-mp="cnijv97sv7vo">
                {title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-forest/70" data-id="element-165" data-mp="7mclaurgkqe8">{text}</p>
            </article>)}
        </div>

        <figure className="mt-7 overflow-hidden rounded-2xl border border-forest/10 bg-white p-2 shadow-sm" data-id="element-166" data-mp="gp3yayzzfu7z">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={{
          visible: {
            transition: {
              staggerChildren: 0.15
            }
          }
        }} className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-id="element-167" data-mp="3p45oahfkpy5m">
            <motion.img variants={{
            hidden: {
              opacity: 0,
              y: 20,
              scale: 0.95
            },
            visible: {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                duration: 0.6,
                ease: 'easeOut'
              }
            }
          }} src="https://cdn.magicpatterns.com/uploads/sQMpXUGXJj7McmPABrv9in/Imagen_2_(2).png" alt="Three colorful fruit and vegetable smoothies" className="aspect-[4/3] w-full rounded-xl object-cover sm:aspect-[3/4]" loading="lazy" data-id="element-168" data-mp="3fryev5196fq6" />
            <motion.img variants={{
            hidden: {
              opacity: 0,
              y: 20,
              scale: 0.95
            },
            visible: {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                duration: 0.6,
                ease: 'easeOut'
              }
            }
          }} src="https://cdn.magicpatterns.com/uploads/ggcXgqMmvSEeqeH6qquaeX/Imagen_1_(3).png" alt="Granola bowl with banana, strawberries, and blueberries" className="aspect-[4/3] w-full rounded-xl object-cover sm:aspect-[3/4]" loading="lazy" data-id="element-169" data-mp="3oqjt4nzoz8l5" />
            <motion.img variants={{
            hidden: {
              opacity: 0,
              y: 20,
              scale: 0.95
            },
            visible: {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                duration: 0.6,
                ease: 'easeOut'
              }
            }
          }} src="https://cdn.magicpatterns.com/uploads/fj8juwrfeFMMgor5fx1ZKB/Imagen_4.jpg" alt="Yogurt parfait with berries and granola" className="aspect-[4/3] w-full rounded-xl object-cover sm:aspect-[3/4]" loading="lazy" data-id="element-170" data-mp="2jtrlquc8mu8t" />
            <motion.video variants={{
            hidden: {
              opacity: 0,
              y: 20,
              scale: 0.95
            },
            visible: {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                duration: 0.6,
                ease: 'easeOut'
              }
            }
          }} src="https://cdn.magicpatterns.com/uploads/i5EG9JYwrpiU5NSWAc1f75/kling_20260715_VIDEO_Animate_th_3006_0.mp4" className="aspect-[4/3] w-full rounded-xl object-cover sm:aspect-[3/4]" autoPlay loop muted playsInline preload="auto" data-id="element-171" data-mp="2xjbkzq6m19dd" />
          </motion.div>
          <figcaption className="px-4 pb-2 pt-4 text-center text-sm font-semibold text-forest/75" data-id="element-172" data-mp="gcl8dqevwpta">
            A full range of satisfying mornings, all inside your guide.
          </figcaption>
        </figure>
      </div>
    </section>;
}